// Simple Free Gift Manager - Single Event Approach
// This version only listens to ONE type of event to prevent conflicts

class SimpleFreeGiftManager {
  constructor(config) {
    this.config = {
      freeProductId: null,
      triggerProductIds: [],
      freeProductHandle: '',
      triggerType: 'specific_products',
      ...config
    };
    
    this.freeProductVariantId = null;
    this.userRemovedFreeGift = false;
    this.isProcessing = false;
    this.lastEventTime = 0;
    
    console.log('🎁 Simple Free Gift Manager: Starting with config:', this.config);
    this.init();
  }
  
  async init() {
    console.log('🎁 Simple Free Gift Manager: Initializing...');
    
    // Get free product info
    await this.getFreeProductVariant();
    
    if (!this.freeProductVariantId) {
      console.error('🎁 Simple Free Gift Manager: No free product variant found');
      return;
    }
    
    // ONLY listen to add-to-cart events (not cart changes)
    this.setupAddToCartListener();
    
    // Setup cart observer for reliable styling
    this.setupCartObserver();
    
    // Check initial cart
    setTimeout(() => this.checkInitialCart(), 2000);
  }
  
  async getFreeProductVariant() {
    if (!this.config.freeProductHandle) return;
    
    try {
      console.log('🎁 Simple Free Gift Manager: Fetching product:', this.config.freeProductHandle);
      const response = await fetch(`/products/${this.config.freeProductHandle}.js`);
      const text = await response.text();
      const product = JSON.parse(text.replace(/^[^{]*/, '').replace(/[^}]*$/, ''));
      
      if (product?.variants?.length > 0) {
        this.freeProductVariantId = product.variants[0].id;
        this.config.freeProductId = product.id;
        console.log('🎁 Simple Free Gift Manager: Product found:', {
          variantId: this.freeProductVariantId,
          productId: this.config.freeProductId,
          title: product.title
        });
      }
    } catch (error) {
      console.error('🎁 Simple Free Gift Manager: Error fetching product:', error);
      // Emergency fallback
      if (this.config.freeProductId) {
        this.freeProductVariantId = this.config.freeProductId;
        console.log('🎁 Simple Free Gift Manager: Using fallback variant ID');
      }
    }
  }
  
  setupAddToCartListener() {
    console.log('🎁 Simple Free Gift Manager: Setting up add-to-cart listener only...');
    
    // ONLY listen for add-to-cart form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.action && form.action.includes('/cart/add')) {
        console.log('🎁 Simple Free Gift Manager: Add to cart detected');
        
        // Reset user removal flag when they add new products
        this.userRemovedFreeGift = false;
        
        // Check cart after a delay
        setTimeout(() => this.handleAddToCart(), 1500);
      }
    });
    
    // Also listen for add-to-cart button clicks as backup
    document.addEventListener('click', (e) => {
      const button = e.target.closest('[data-add-to-cart], .btn-add-to-cart, .add-to-cart');
      if (button) {
        const form = button.closest('form');
        if (form && form.action && form.action.includes('/cart/add')) {
          console.log('🎁 Simple Free Gift Manager: Add to cart button clicked');
          this.userRemovedFreeGift = false;
          setTimeout(() => this.handleAddToCart(), 1500);
        }
      }
    });
    
    // Listen for manual removals ONLY
    document.addEventListener('click', (e) => {
      const removeButton = e.target.closest('.ajaxcart__qty--minus');
      if (removeButton) {
        const input = removeButton.parentNode.querySelector('input[name="updates[]"]');
        if (input && input.value === '1') { // If they're reducing from 1 to 0
          setTimeout(() => this.checkForManualRemoval(), 500);
        }
      }
    });
  }
  
  // NEW METHOD: Setup cart observer for automatic styling
  setupCartObserver() {
    console.log('🎁 Simple Free Gift Manager: Setting up cart observer...');
    
    // Try multiple possible cart container selectors
    const possibleSelectors = [
      '.ajaxcart',
      '.cart-drawer',
      '.mini-cart',
      '.cart-items',
      '[data-cart]',
      '.cart-content',
      '.drawer__content',
      '#CartDrawer'
    ];
    
    let cartContainer = null;
    for (const selector of possibleSelectors) {
      cartContainer = document.querySelector(selector);
      if (cartContainer) {
        console.log('🎁 Simple Free Gift Manager: Found cart container:', selector);
        break;
      }
    }
    
    if (!cartContainer) {
      console.warn('🎁 Simple Free Gift Manager: No cart container found, will retry...');
      // Retry after a delay in case cart loads later
      setTimeout(() => this.setupCartObserver(), 3000);
      return;
    }
    
    // Create the observer
    this.cartObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach((mutation) => {
        // Check if cart rows were added/removed or if content changed
        if (mutation.type === 'childList' && mutation.target.closest('.ajaxcart__row')) {
          shouldUpdate = true;
        } else if (mutation.type === 'attributes' && mutation.target.classList.contains('ajaxcart__row')) {
          shouldUpdate = true;
        } else if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
          // Check if any added/removed nodes contain cart rows
          const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
          if (nodes.some(node => node.nodeType === 1 && (node.classList?.contains('ajaxcart__row') || node.querySelector?.('.ajaxcart__row')))) {
            shouldUpdate = true;
          }
        }
      });
      
      if (shouldUpdate) {
        console.log('🎁 Simple Free Gift Manager: Cart change detected by observer');
        // Debounce the styling calls to prevent excessive updates
        clearTimeout(this.styleTimeout);
        this.styleTimeout = setTimeout(() => this.styleFreeGifts(), 300);
      }
    });
    
    // Start observing
    this.cartObserver.observe(cartContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });
    
    console.log('🎁 Simple Free Gift Manager: Cart observer started');
  }
  
  async checkForManualRemoval() {
    const cart = await this.getCurrentCart();
    if (!cart) return;
    
    const hasFreeProduct = cart.items.some(item => this.isFreeGiftItem(item));
    const hasTriggerProduct = cart.items.some(item => 
      !this.isFreeGiftItem(item) && 
      (this.config.triggerType === 'all_products' || this.config.triggerProductIds.includes(item.product_id))
    );
    
    // If they removed the free gift but still have trigger products
    if (!hasFreeProduct && hasTriggerProduct) {
      console.log('🎁 Simple Free Gift Manager: 🚫 User manually removed free gift');
      this.userRemovedFreeGift = true;
    }
  }
  
  async handleAddToCart() {
    // Prevent rapid-fire events
    const now = Date.now();
    if (now - this.lastEventTime < 2000) {
      console.log('🎁 Simple Free Gift Manager: Skipping rapid event');
      return;
    }
    this.lastEventTime = now;
    
    if (this.isProcessing) {
      console.log('🎁 Simple Free Gift Manager: Already processing');
      return;
    }
    
    console.log('🎁 Simple Free Gift Manager: Handling add to cart...');
    
    const cart = await this.getCurrentCart();
    if (!cart) return;
    
    console.log('🎁 Simple Free Gift Manager: Cart items:', cart.items.length);
    
    const hasTriggerProduct = cart.items.some(item => {
      const isTrigger = !this.isFreeGiftItem(item) && 
        (this.config.triggerType === 'all_products' || this.config.triggerProductIds.includes(item.product_id));
      
      if (isTrigger) {
        console.log('🎁 Simple Free Gift Manager: Found trigger product:', item.title);
      }
      return isTrigger;
    });
    
    const hasFreeProduct = cart.items.some(item => {
      const isFree = this.isFreeGiftItem(item);
      if (isFree) {
        console.log('🎁 Simple Free Gift Manager: Found free product:', item.title);
      }
      return isFree;
    });
    
    console.log('🎁 Simple Free Gift Manager: Analysis:', {
      hasTriggerProduct,
      hasFreeProduct,
      userRemovedFreeGift: this.userRemovedFreeGift
    });
    
    // Only add free gift if:
    // 1. Has trigger product
    // 2. No free product currently
    // 3. User hasn't manually removed it
    if (hasTriggerProduct && !hasFreeProduct && !this.userRemovedFreeGift) {
      console.log('🎁 Simple Free Gift Manager: ✅ Adding free gift');
      await this.addFreeGiftToCart();
    } else if (hasTriggerProduct && hasFreeProduct) {
      console.log('🎁 Simple Free Gift Manager: ✅ Cart already has free gift');
    } else if (this.userRemovedFreeGift) {
      console.log('🎁 Simple Free Gift Manager: 🚫 Respecting user removal');
    } else {
      console.log('🎁 Simple Free Gift Manager: ℹ️ No action needed');
    }
    
    // Apply styling after checking cart (observer will also catch this)
    // setTimeout(() => this.styleFreeGifts(), 500);
  }
  
  async checkInitialCart() {
    console.log('🎁 Simple Free Gift Manager: Checking initial cart...');
    await this.handleAddToCart();
  }
  
  isFreeGiftItem(item) {
    return item.product_id === this.config.freeProductId ||
           item.variant_id === this.freeProductVariantId ||
           (item.properties && item.properties._free_gift === 'true');
  }
  
  async getCurrentCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('🎁 Simple Free Gift Manager: Error fetching cart:', error);
      return null;
    }
  }
  
  async addFreeGiftToCart() {
    if (this.isProcessing) return;
    
    this.isProcessing = true;
    console.log('🎁 Simple Free Gift Manager: Adding free gift...');
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: this.freeProductVariantId,
          quantity: 1,
          properties: {
            '_free_gift': 'true',
            '_auto_added': 'true'
          }
        })
      });
      
      if (response.ok) {
        console.log('🎁 Simple Free Gift Manager: ✅ Free gift added successfully!');
        this.showNotification('🎁 Free gift added to your cart!');
        
        // Refresh cart display
        if (typeof window.ajaxCart !== 'undefined' && window.ajaxCart.load) {
          setTimeout(() => window.ajaxCart.load(), 500);
        }
        
        // Apply styling (observer will catch cart changes automatically)
        // setTimeout(() => this.styleFreeGifts(), 1000);
      } else {
        console.error('🎁 Simple Free Gift Manager: Failed to add free gift');
      }
    } catch (error) {
      console.error('🎁 Simple Free Gift Manager: Error adding free gift:', error);
    } finally {
      this.isProcessing = false;
    }
  }
  
  // Improved styling method with better cart detection
  styleFreeGifts() {
    console.log('🎨 Styling free gifts...');
    
    // Find all items that could be free gifts (have _free_gift property)
    const possibleSelectors = [
      '.ajaxcart__row:has(.ajaxcart__product-meta[data-key="_free_gift"])',
      '.ajaxcart__row [data-key="_free_gift"]',
      '.cart-item:has([data-key="_free_gift"])',
      '.line-item:has([data-key="_free_gift"])'
    ];
    
    let potentialFreeGiftRows = [];
    
    // Try CSS :has() selector first (modern browsers)
    try {
      potentialFreeGiftRows = Array.from(document.querySelectorAll(possibleSelectors[0]));
    } catch (e) {
      // Fallback for browsers without :has() support
      console.log('🎨 Using fallback selector method');
      const metaElements = document.querySelectorAll('[data-key="_free_gift"]');
      potentialFreeGiftRows = Array.from(metaElements).map(meta => meta.closest('.ajaxcart__row, .cart-item, .line-item')).filter(Boolean);
    }
    
    if (potentialFreeGiftRows.length === 0) {
      console.log('🎨 No potential free gift rows found');
      return;
    }
    
    // Filter to only items that are actually FREE (price = 0)
    const freeGiftRows = potentialFreeGiftRows.filter(row => {
      const hasFreeGift = row.querySelector('[data-key="_free_gift"]');
      if (!hasFreeGift) return false;
      
      // Check if the price is actually 0 - look for the main price span (not strikethrough)
      const priceContainer = row.querySelector('.ajaxcart__price');
      if (!priceContainer) return false;
      
      // Look for the main price span (not the strikethrough one)
      const priceSpans = priceContainer.querySelectorAll('span');
      let currentPrice = null;
      
      // Find the span that's NOT inside a <s> tag (strikethrough)
      for (const span of priceSpans) {
        if (!span.closest('s') && !span.classList.contains('ajaxcart-item__price-strikethrough')) {
          currentPrice = span.textContent || span.innerText || '';
          break;
        }
      }
      
      if (!currentPrice) {
        // Fallback: get all text from price container and try to find current price
        const priceText = priceContainer.textContent || priceContainer.innerText || '';
        // Look for patterns like $0.00, $0, etc. that aren't strikethrough
        const priceMatches = priceText.match(/\$(\d+\.?\d*)/g);
        if (priceMatches && priceMatches.length > 0) {
          // Use the last price found (usually the current price after strikethrough)
          currentPrice = priceMatches[priceMatches.length - 1];
        }
      }
      
      if (currentPrice) {
        const priceValue = currentPrice.replace(/[^\d.,]/g, '').replace(',', '.');
        const numericPrice = parseFloat(priceValue) || 0;
        
        console.log('🎨 Item price check:', {
          priceText: currentPrice.trim(),
          priceValue,
          numericPrice,
          isFree: numericPrice === 0
        });
        
        return numericPrice === 0;
      }
      
      // Additional fallback: check for "Free Gift With Purchase" text
      const discountText = row.querySelector('.ajaxcart-item__discount');
      if (discountText && discountText.textContent.includes('Free Gift')) {
        console.log('🎨 Found free gift by discount text');
        return true;
      }
      
      return false;
    });
    
    // Count total cart rows using multiple possible selectors
    const rowSelectors = ['.ajaxcart__row', '.cart-item', '.line-item', '.cart-drawer__item'];
    let totalCartRows = 0;
    
    for (const selector of rowSelectors) {
      const rows = document.querySelectorAll(selector);
      if (rows.length > 0) {
        totalCartRows = rows.length;
        break;
      }
    }
    
    console.log('🎨 Found:', freeGiftRows.length, 'truly free gift rows out of', totalCartRows, 'total rows');
    
    // Reset styling for ALL potential free gift rows first
    potentialFreeGiftRows.forEach(row => {
      if (!row) return;
      
      // Remove any existing styling
      row.classList.remove('free-gift-styled');
      row.style.backgroundColor = '';
      row.style.borderLeft = '';
      row.style.padding = '';
      row.style.margin = '';
      row.style.borderRadius = '';
      
      // Find and reset elements
      const priceSelectors = ['.ajaxcart__price span', '.cart-item__price', '.line-item__price', '.price'];
      const qtySelectors = ['.ajaxcart__qty_container', '.cart-item__qty', '.line-item__qty', '.quantity-selector'];
      const nameSelectors = ['.ajaxcart__product-name', '.cart-item__name', '.line-item__name', '.product-title'];
      
      let priceSpans = [];
      let qtyContainer = null;
      let productName = null;
      
      for (const selector of priceSelectors) {
        priceSpans = Array.from(row.querySelectorAll(selector));
        if (priceSpans.length > 0) break;
      }
      
      for (const selector of qtySelectors) {
        qtyContainer = row.querySelector(selector);
        if (qtyContainer) break;
      }
      
      for (const selector of nameSelectors) {
        productName = row.querySelector(selector);
        if (productName) break;
      }
      
      // Reset elements
      priceSpans.forEach(span => span.style.display = '');
      if (qtyContainer) qtyContainer.style.display = '';
      if (productName) {
        const existingBadge = productName.querySelector('.free-gift-badge');
        if (existingBadge) existingBadge.remove();
      }
    });
    
    // Now apply styling only to truly free items (price = 0) and only if multiple items in cart
    if (totalCartRows > 1) {
      freeGiftRows.forEach(row => {
        if (!row) return;
        
        console.log('🎨 Applying free gift styling to zero-price item');
        
        // Find elements
        const priceSelectors = ['.ajaxcart__price span', '.cart-item__price', '.line-item__price', '.price'];
        const qtySelectors = ['.ajaxcart__qty_container', '.cart-item__qty', '.line-item__qty', '.quantity-selector'];
        const nameSelectors = ['.ajaxcart__product-name', '.cart-item__name', '.line-item__name', '.product-title'];
        
        let priceSpans = [];
        let qtyContainer = null;
        let productName = null;
        
        for (const selector of priceSelectors) {
          priceSpans = Array.from(row.querySelectorAll(selector));
          if (priceSpans.length > 0) break;
        }
        
        for (const selector of qtySelectors) {
          qtyContainer = row.querySelector(selector);
          if (qtyContainer) break;
        }
        
        for (const selector of nameSelectors) {
          productName = row.querySelector(selector);
          if (productName) break;
        }
        
        // Apply styling
        row.classList.add('free-gift-styled');
        row.style.backgroundColor = '#FEF5E6';
        row.style.borderLeft = '4px solid #845D33';
        row.style.padding = '12px';
        row.style.margin = '8px 0';
        row.style.borderRadius = '6px';
        
        // Hide price
        priceSpans.forEach(span => span.style.display = 'none');
        
        // Hide quantity container
        if (qtyContainer) qtyContainer.style.display = 'none';
        
        // Add badge
        if (productName && !productName.querySelector('.free-gift-badge')) {
          const badge = document.createElement('span');
          badge.className = 'free-gift-badge';
          badge.textContent = ' • Free Gift';
          badge.style.color = '#845D33';
          badge.style.fontWeight = 'normal';
          badge.style.fontSize = '12px';
          badge.style.fontStyle = 'italic';
          productName.appendChild(badge);
        }
      });
    } else {
      console.log('🎨 No styling applied (single item in cart)');
    }
  }
  
  showNotification(message) {
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #28a745;
      color: white;
      padding: 15px 20px;
      border-radius: 5px;
      z-index: 99999;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      font-family: Arial, sans-serif;
      font-size: 14px;
      max-width: 300px;
    `;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    setTimeout(() => notification.remove(), 4000);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('🎁 Simple Free Gift Manager: DOM ready');
  
  const config = {
    freeProductId: window.shopify_free_gift_product_id || null,
    freeProductHandle: window.shopify_free_gift_handle || '',
    triggerType: window.shopify_trigger_type || 'specific_products',
    triggerProductIds: window.shopify_trigger_products || []
  };
  
  const shouldInitialize = config.freeProductHandle && (
    config.triggerType === 'all_products' || 
    (config.triggerType === 'specific_products' && config.triggerProductIds.length > 0)
  );
  
  if (shouldInitialize) {
    window.simpleFreeGiftManager = new SimpleFreeGiftManager(config);
    console.log('🎁 Simple Free Gift Manager: Initialized!');
  } else {
    console.log('🎁 Simple Free Gift Manager: Not initialized - missing config');
  }
});

// Global function for external calls
window.styleFreeGifts = function() {
  if (window.simpleFreeGiftManager && window.simpleFreeGiftManager.styleFreeGifts) {
    window.simpleFreeGiftManager.styleFreeGifts();
  }
};

window.SimpleFreeGiftManager = SimpleFreeGiftManager;

// Basic CSS for free gift meta hiding only
const freeGiftCSS = `
/* Hide free gift meta properties */
.ajaxcart__product-meta[data-key="_free_gift"],
.ajaxcart__product-meta[data-key="_auto_added"] {
  display: none !important;
}

/* Also hide these meta properties in different cart layouts */
.cart-item__property[data-key="_free_gift"],
.cart-item__property[data-key="_auto_added"],
.line-item__property[data-key="_free_gift"],
.line-item__property[data-key="_auto_added"] {
  display: none !important;
}
`;

// Add the CSS to the page
const style = document.createElement('style');
style.textContent = freeGiftCSS;
document.head.appendChild(style);

console.log('🎨 Free Gift CSS: Basic styling applied');